import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SignaturePad from "signature_pad";
import { getWaiver, signWaiver, getSignaturePdfUploadUrl, storeSignaturePdfKey } from "../../../services/api/waivers";
import { fetchCurrentUserProfile, hasLocallyValidSession } from "../../../services/auth/auth-session";
import LoginDialog from "../../auth/login-dialog";
import { loadErrorMessage } from "../../../utils/api-error";

export default function WaiverSignView() {
  const { leagueId, waiverId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [isAuthed, setIsAuthed] = React.useState(hasLocallyValidSession());
  const [loginOpen, setLoginOpen] = React.useState(false);

  const [waiver, setWaiver] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const [userProfile, setUserProfile] = React.useState(null);
  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [typedFullName, setTypedFullName] = React.useState("");
  const [accepted, setAccepted] = React.useState(false);
  const [signTab, setSignTab] = React.useState(0); // 0 = type, 1 = draw
  const [submitState, setSubmitState] = React.useState({ loading: false, error: "", success: false, alreadySigned: false });

  // Drawn signature
  const canvasRef = React.useRef(null);
  const sigPadRef = React.useRef(null);
  const [hasDrawn, setHasDrawn] = React.useState(false);

  React.useEffect(() => {
    if (!leagueId || !waiverId) return;
    if (!isAuthed) {
      setLoading(false);
      return;
    }

    setLoading(true);

    fetchCurrentUserProfile().then((profile) => {
      if (profile) {
        setUserProfile(profile);
        setEmail(profile.email ?? "");
        setFirstName(profile.firstName ?? "");
        setLastName(profile.lastName ?? "");
        const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ");
        setTypedFullName(fullName);
      }
    });

    getWaiver(leagueId, waiverId)
      .then((data) => {
        setWaiver(data);
        if (data.isSigned) {
          setSubmitState({ loading: false, error: "", success: true, alreadySigned: true });
        }
      })
      .catch((err) => setLoadError(loadErrorMessage(err, "Failed to load waiver.")))
      .finally(() => setLoading(false));
  }, [leagueId, waiverId, isAuthed]);

  // Initialize SignaturePad when draw tab is selected
  React.useEffect(() => {
    if (signTab !== 1 || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const pad = new SignaturePad(canvas, {
      backgroundColor: isDark ? "rgb(30, 30, 30)" : "rgb(255, 255, 255)",
      penColor: isDark ? "rgb(255, 255, 255)" : "rgb(0, 0, 0)",
    });
    sigPadRef.current = pad;

    pad.addEventListener("endStroke", () => {
      setHasDrawn(!pad.isEmpty());
    });

    // Resize canvas to match its display size
    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const data = pad.toData();
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      pad.clear();
      if (data.length > 0) pad.fromData(data);
    };

    resizeCanvas();
    const ro = new ResizeObserver(resizeCanvas);
    ro.observe(canvas);

    return () => {
      ro.disconnect();
      pad.off();
    };
  }, [signTab, isDark]);

  const handleClearSignature = () => {
    sigPadRef.current?.clear();
    setHasDrawn(false);
  };

  const getSignatureDataUrl = () => {
    if (signTab === 1 && sigPadRef.current && !sigPadRef.current.isEmpty()) {
      return sigPadRef.current.toDataURL("image/png");
    }
    return null;
  };

  /** Generates the PDF blob and uploads it to S3 via presigned URL, then stores the key. */
  const uploadPdfToS3 = async (newSignatureId) => {
    try {
      const { uploadUrl, s3Key } = await getSignaturePdfUploadUrl(leagueId, waiverId, newSignatureId);
      const html2pdf = (await import("html2pdf.js")).default;
      const signedAt = new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
      const signerName = typedFullName.trim() || [firstName, lastName].filter(Boolean).join(" ");
      const signatureDataUrl = getSignatureDataUrl();
      const signatureBlock = signatureDataUrl
        ? `<img src="${signatureDataUrl}" style="max-width:260px;border-bottom:1px solid #333;display:block;" />`
        : `<p style="font-family:'Brush Script MT',cursive;font-size:28px;margin:0;">${signerName}</p>`;

      const el = document.createElement("div");
      el.innerHTML = `
        <div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.6;color:#111;max-width:800px;margin:0 auto;padding:40px;">
          <h1 style="font-size:20px;margin-bottom:4px;">${waiver.name}</h1>
          <p style="color:#666;font-size:12px;margin-top:0;">Version ${waiver.version}</p>
          <hr style="border:none;border-top:1px solid #ddd;margin:16px 0;" />
          ${waiver.contentHtml}
          <hr style="border:none;border-top:1px solid #ddd;margin:32px 0 16px;" />
          <h3 style="font-size:14px;margin-bottom:8px;">Signature</h3>
          ${signatureBlock}
          <p style="margin-top:8px;font-size:12px;color:#555;">
            Signed by: <strong>${signerName}</strong><br/>
            Email: ${email || "N/A"}<br/>
            Date: ${signedAt}
          </p>
          <p style="font-size:11px;color:#888;margin-top:24px;">
            This document was signed electronically in accordance with the E-SIGN Act and UETA.
          </p>
        </div>`;
      document.body.appendChild(el);

      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename: `waiver.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        })
        .from(el)
        .outputPdf("blob");

      document.body.removeChild(el);

      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: pdfBlob,
      });

      await storeSignaturePdfKey(leagueId, waiverId, newSignatureId, s3Key);
    } catch {
      // S3 upload is best-effort; player can still download manually
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!accepted) {
      setSubmitState((s) => ({ ...s, error: "You must check the acceptance box." }));
      return;
    }
    if (signTab === 0 && !typedFullName.trim()) {
      setSubmitState((s) => ({ ...s, error: "Please type your full name to sign." }));
      return;
    }
    if (signTab === 1 && (!sigPadRef.current || sigPadRef.current.isEmpty())) {
      setSubmitState((s) => ({ ...s, error: "Please draw your signature." }));
      return;
    }
    if (!userProfile && !email.trim()) {
      setSubmitState((s) => ({ ...s, error: "Email is required." }));
      return;
    }

    setSubmitState({ loading: true, error: "", success: false, alreadySigned: false });

    try {
      const signatureDataUrl = getSignatureDataUrl();
      const payload = {
        acceptedCheckbox: true,
        typedFullName: typedFullName.trim() || [firstName, lastName].filter(Boolean).join(" "),
        ...(signatureDataUrl ? { signatureImage: signatureDataUrl } : {}),
        ...(userProfile
          ? {}
          : {
              email: email.trim().toLowerCase(),
              firstName: firstName.trim(),
              lastName: lastName.trim(),
            }),
      };
      const result = await signWaiver(leagueId, waiverId, payload);
      const newSignatureId = result.id ?? null;
      setSubmitState({ loading: false, error: "", success: true, alreadySigned: result.alreadySigned ?? false });

      // Kick off background PDF upload to S3 (non-blocking — failure is silent to user)
      if (newSignatureId && !result.alreadySigned) {
        uploadPdfToS3(newSignatureId).catch(() => {});
      }
    } catch (err) {
      setSubmitState({
        loading: false,
        error: err instanceof Error ? err.message : "Failed to sign waiver.",
        success: false,
        alreadySigned: false,
      });
    }
  };

  const handleDownloadPdf = async () => {
    if (!waiver) return;
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const signedAt = new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
      const signerName = typedFullName.trim() || [firstName, lastName].filter(Boolean).join(" ");
      const signatureDataUrl = getSignatureDataUrl();

      const signatureBlock = signatureDataUrl
        ? `<img src="${signatureDataUrl}" style="max-width:260px;border-bottom:1px solid #333;display:block;" />`
        : `<p style="font-family:'Brush Script MT',cursive;font-size:28px;margin:0;">${signerName}</p>`;

      const pdfContent = `
        <div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.6;color:#111;max-width:800px;margin:0 auto;padding:40px;">
          <h1 style="font-size:20px;margin-bottom:4px;">${waiver.name}</h1>
          <p style="color:#666;font-size:12px;margin-top:0;">Version ${waiver.version}</p>
          <hr style="border:none;border-top:1px solid #ddd;margin:16px 0;" />
          ${waiver.contentHtml}
          <hr style="border:none;border-top:1px solid #ddd;margin:32px 0 16px;" />
          <h3 style="font-size:14px;margin-bottom:8px;">Signature</h3>
          ${signatureBlock}
          <p style="margin-top:8px;font-size:12px;color:#555;">
            Signed by: <strong>${signerName}</strong><br/>
            Email: ${email || "N/A"}<br/>
            Date: ${signedAt}
          </p>
          <p style="font-size:11px;color:#888;margin-top:24px;">
            This document was signed electronically in accordance with the Electronic Signatures in Global and
            National Commerce Act (E-SIGN Act) and the Uniform Electronic Transactions Act (UETA).
          </p>
        </div>
      `;

      const element = document.createElement("div");
      element.innerHTML = pdfContent;
      document.body.appendChild(element);

      await html2pdf()
        .set({
          margin: 0,
          filename: `waiver-${waiver.name.replace(/\s+/g, "-").toLowerCase()}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        })
        .from(element)
        .save();

      document.body.removeChild(element);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const handleContinue = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(`/leagues/${leagueId}/signup`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAuthed) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <LoginDialog
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
          onSuccess={() => { setIsAuthed(true); setLoginOpen(false); window.dispatchEvent(new Event("puck-pickup-auth-change")); }}
        />
        <Stack spacing={3} alignItems="center" sx={{ textAlign: "center" }}>
          <Typography variant="h5">Sign in to sign this waiver</Typography>
          <Typography color="text.secondary">
            You need a Puck-Pickup account to sign waivers electronically.
          </Typography>
          <Button variant="contained" size="large" onClick={() => setLoginOpen(true)}>
            Sign in
          </Button>
          <Button variant="text" onClick={() => navigate(-1)}>Go back</Button>
        </Stack>
      </Container>
    );
  }

  if (loadError || !waiver) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{loadError || "Waiver not found."}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>Go back</Button>
      </Container>
    );
  }

  if (submitState.success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Typography variant="h4" component="h3">Waiver signed</Typography>
          <Alert severity="success">
            {submitState.alreadySigned
              ? "You have already signed this waiver."
              : `You've successfully signed "${waiver.name}".`}
          </Alert>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button variant="contained" onClick={handleContinue}>
              {returnTo ? "Continue" : "Back to signup"}
            </Button>
            {!submitState.alreadySigned && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPdf}
              >
                Download PDF copy
              </Button>
            )}
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3} component="form" onSubmit={handleSubmit}>
        <Stack spacing={0.5}>
          <Typography variant="h4" component="h3">{waiver.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Version {waiver.version} · Please read carefully before signing
          </Typography>
        </Stack>

        <Card variant="outlined">
          <CardContent
            sx={{
              maxHeight: 360,
              overflowY: "auto",
              "& *": { maxWidth: "100%" },
            }}
          >
            <Box
              dangerouslySetInnerHTML={{ __html: waiver.contentHtml }}
              sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
            />
          </CardContent>
        </Card>

        {!userProfile && (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Your information</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Sign</Typography>

              <Tabs
                value={signTab}
                onChange={(_, v) => setSignTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
              >
                <Tab icon={<KeyboardIcon fontSize="small" />} iconPosition="start" label="Type name" />
                <Tab icon={<DrawIcon fontSize="small" />} iconPosition="start" label="Draw signature" />
              </Tabs>

              {signTab === 0 && (
                <TextField
                  label="Type your full legal name"
                  value={typedFullName}
                  onChange={(e) => setTypedFullName(e.target.value)}
                  required
                  fullWidth
                  helperText="Your typed name serves as your electronic signature"
                />
              )}

              {signTab === 1 && (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Sign your name in the box below using your mouse or finger.
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      overflow: "hidden",
                      bgcolor: isDark ? "grey.900" : "grey.50",
                      height: 160,
                      position: "relative",
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      style={{ width: "100%", height: "100%", display: "block", touchAction: "none" }}
                    />
                    {!hasDrawn && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <Typography variant="body2" color="text.disabled">
                          Sign here
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={handleClearSignature}
                    sx={{ alignSelf: "flex-end" }}
                  >
                    Clear
                  </Button>
                </Stack>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                  />
                }
                label="I have read and agree to the terms of this waiver"
              />
            </Stack>
          </CardContent>
        </Card>

        {submitState.error && (
          <Alert severity="error" onClose={() => setSubmitState((s) => ({ ...s, error: "" }))}>
            {submitState.error}
          </Alert>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={
              submitState.loading ||
              !accepted ||
              (signTab === 0 && !typedFullName.trim()) ||
              (signTab === 1 && !hasDrawn)
            }
          >
            {submitState.loading ? "Signing…" : "Sign Waiver"}
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
